import { SYSTEM_USER_MANAGEMENT } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { ActionLogInjection } from "features/actionLogManagement/di/ActionLogInjection";
import { isEmpty, isString } from "lodash";
import { injectable } from "tsyringe";
import { DateFormat } from "utils/format/DateFormat";
import { strings } from "utils/localizedStrings";
import { ActionLogDetailEntity } from "../entities/ActionLogDetailEntity";
import { ActionLogRequestEntity } from "../entities/ActionLogRequestEntity";
import { ActionLogResponseEntity } from "../entities/ActionLogResponseEntity";
import { IActionLogRepository } from "../repositories/IActionLogRepository";

@injectable()
export class ActionLogUseCase {
  private actionLogRepo: IActionLogRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.actionLogRepo = ActionLogInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private mapInput(value: ActionLogRequestEntity) {
    const fromDate =
      value.isNeedDate && !isEmpty(value.created)
        ? DateFormat.dateRendering(value.created, "from")
        : value?.fromDate;

    const toDate =
      value.isNeedDate && !isEmpty(value.created)
        ? DateFormat.dateRendering(value.created, "to")
        : value?.toDate;

    Object.keys(value).forEach((rawKey) => {
      const key = rawKey as keyof ActionLogRequestEntity;
      if (
        value[key] === strings.GlobalLabels.All ||
        (isString(value[key]) && value[key] === "") ||
        typeof value[key] === "undefined"
      ) {
        delete value[key];
      }
    });

    return new ActionLogRequestEntity({
      ...value,
      search:
        value.search ||
        value.name ||
        value.code ||
        value.path ||
        value.input ||
        value.output,
      fromDate,
      toDate,
    });
  }

  async getList(
    params: ActionLogRequestEntity,
  ): Promise<ListBaseApiResponseModel<ActionLogResponseEntity>> {
    const updatedParams = this.mapInput(params);
    delete updatedParams?.created;
    delete updatedParams?.isNeedDate;
    return await this.actionLogRepo.getList(updatedParams);
  }

  async getDetail(
    params: ActionLogRequestEntity,
  ): Promise<BaseApiResponseModel<ActionLogDetailEntity>> {
    return await this.actionLogRepo.getDetail(params);
  }

  async getUserList(
    params: BaseUserRequestModel,
  ): Promise<Array<BaseUserResponseModel>> {
    return this.filterUseCase.fetchListWithFeature({
      path: SYSTEM_USER_MANAGEMENT.FILTER,
      isNeedFeature: true,
      params,
      isForceRefresh: true,
    });
  }
}
